import * as PIXI from 'pixi.js';

import { IArea, IStateAwareArea, SpriteType } from 'types/map';
import { isDistrict } from 'components/Map/util';
import GlobalSpriteMap from 'components/Map/PixiMap/GlobalSpriteMap';
import { getPixiContainer, getPixiMainRenderer } from './PixiSingletons';
import getPositionForSprite from 'components/Map/util/getPositionForSprite';
import { MAP_HEIGHT, MAP_WIDTH } from 'components/Map/constants';

export const AVAILABLE_SPRITE_CACHE: { [key: string]: PIXI.Sprite } = {};
export const SELECTION_SPRITE_CACHE: { [key: string]: PIXI.Sprite } = {};
export const MAIN_DESTINATION_SPRITE_CACHE: { [key: string]: PIXI.Sprite } = {};
export const MAIN_DESTINATION_SELECTION_SPRITE_CACHE: { [key: string]: PIXI.Sprite } = {};

const FOREGROUND_STALAGMITE_SPRITE_HEIGHT = 510;

export function addBackgroundSprite(sprite: PIXI.Sprite, aOrB: string) {
  sprite.scale = new PIXI.Point(0.5, 0.5);
  if (aOrB === 'a' || aOrB === 'stalagmites-a') {
    sprite.position = new PIXI.Point(0, 0);
  } else {
    sprite.position = new PIXI.Point(MAP_WIDTH / 4, 0);
  }
  getPixiContainer().addChild(sprite);
}

export function addForegroundSprite(sprite: PIXI.Sprite, aOrB: string) {
  sprite.scale = new PIXI.Point(0.5, 0.5);
  const y = (MAP_HEIGHT/2 - FOREGROUND_STALAGMITE_SPRITE_HEIGHT/2);
  if (aOrB === 'a' || aOrB === 'stalagmites-a') {
    sprite.position = new PIXI.Point(0, y);
  } else {
    sprite.position = new PIXI.Point(MAP_WIDTH / 4, y);
  }
  getPixiContainer().addChild(sprite);
}

export async function addAreaSpriteToContainer(area: IArea, spriteType: SpriteType) {
  console.info(`addAreaSpriteToContainer(${area.areaKey}, ${spriteType})`);
  const pixiContainer = getPixiContainer();
  // Sprite has already been added, go away
  if (isSpriteInCache(area, spriteType)) {
    return;
  }

  // Only districts have main destination art
  if (!isDistrict(area) && (spriteType === 'main-destination' || spriteType === 'main-destination-selection')) {
    return;
  }

  const sprite = await getSpriteFromCache(area, spriteType);

  if (sprite) {
    sprite.position = getPositionForSprite(area, spriteType);
    pixiContainer.addChild(sprite);
  } else {
    throw new Error(`Cache miss on ${area.areaKey}:${spriteType}`);
  }
}

export function getCacheForSpriteType(whatKind: SpriteType) {
  switch (whatKind) {
    case 'available':
      return AVAILABLE_SPRITE_CACHE;
    case 'selection':
      return SELECTION_SPRITE_CACHE;
    case 'main-destination-selection':
      return MAIN_DESTINATION_SELECTION_SPRITE_CACHE;
    case 'main-destination':
    default:
      return MAIN_DESTINATION_SPRITE_CACHE;
  }
}

export function forceRender() {
  getPixiMainRenderer().render(getPixiContainer());
}

export async function getSpriteFromCache(area: IArea, whatKind: SpriteType) {
  const { areaKey, name: areaName } = area;
  const cache = getCacheForSpriteType(whatKind);
  if (!cache[areaKey]) {
    const sprite = await GlobalSpriteMap.get(area, whatKind);
    if (sprite) {
      cache[areaKey] = sprite;
    } else {
      // console.error(`No sprite for area '${areaKey || areaName}' in state '${whatKind}'`);
      throw new Error(`No sprite for area '${areaKey || areaName}' in state '${whatKind}'`);
    }
  }
  return cache[areaKey];
}

export function isSpriteInCache(area: IArea, whatKind: SpriteType) {
  const cache = getCacheForSpriteType(whatKind);
  return !!cache[area.areaKey];
}

export async function updateSpriteForArea(area: IStateAwareArea) {
  const { areaKey } = area;

  // Non-mappable areas have no sprite
  if (!area.isMappable) {
    return;
  }

  // Landmarks have no sprite
  if (area.isLandmark) {
    return;
  }

  // Add any sprites we don't already have
  await Promise.all(addAreaSpritesToContainer(area));

  // Set up sprites for this area:
  // - darken unlit districts
  // - show/hide districts' main destinations and main destination selection glows
  if (area.isDistrict) {
    // Darken unlit districts
    AVAILABLE_SPRITE_CACHE[areaKey].filters[0].enabled = !area.isLit;
    // Hide main destinations for districts we haven't unlocked
    MAIN_DESTINATION_SPRITE_CACHE[areaKey].filters[1].enabled = !area.shouldShowMainDestination;
    // Hide main destination selection sprites to start with
    MAIN_DESTINATION_SELECTION_SPRITE_CACHE[areaKey].filters[1].enabled = true;
  }
}

/**
 * Add all missing sprites for an area to the PIXI container.
 * @param area
 */
function addAreaSpritesToContainer(area: IStateAwareArea): Promise<void>[] {
  const promises = [];
  if (!isSpriteInCache(area, 'available')) {
    promises.push(addAreaSpriteToContainer(area, 'available'));
  }
  if (!isSpriteInCache(area, 'selection')) {
    promises.push(addAreaSpriteToContainer(area, 'selection'));
  }
  if (area.isDistrict) {
  }
  if (!isSpriteInCache(area, 'main-destination')) {
    promises.push(addAreaSpriteToContainer(area, 'main-destination'));
  }
  if (!isSpriteInCache(area, 'main-destination-selection')) {
    promises.push(addAreaSpriteToContainer(area, 'main-destination-selection'));
  }
  return promises;
}
